import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { NormalizationDetails } from 'benchmarking-domain/normalization/normalization-details';
import Project from 'benchmarking-domain/project';
import ProjectProgressStatistics from 'benchmarking-domain/project-progress-statistics';
import { debounceLeadingAsync } from 'helpers/function';
import { NumberHelper } from 'helpers/number';
import useApi from 'hooks/use-api';
import { useOrganisation } from 'hooks/use-organisation';

import { NormalizationStepAccordion } from './normalization-step-accordion';

export interface NormalizationViewerProps {
  project: Project;
  projectProgressStatistics?: ProjectProgressStatistics;
}

const RoundingPrecision = 4;

const NormalizationViewer: React.FC<NormalizationViewerProps> = memo(({ project, projectProgressStatistics }) => {
  const { organisationId } = useOrganisation();
  const { getNormalizationDetailsForProject } = useApi();

  const [normalizationData, setNormalizationData] = useState<NormalizationDetails | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNormalizationUpdateDebounce = React.useCallback(
    debounceLeadingAsync(async () => {
      if (!organisationId) {
        return;
      }

      try {
        const data = await getNormalizationDetailsForProject(project.frameworkId, organisationId, project.id);
        setNormalizationData(data);
      } catch (err) {
        setNormalizationData(undefined);
      }

      setLoading(false);
    }, 1000),
    [getNormalizationDetailsForProject, setNormalizationData, setLoading]
  );

  useEffect(() => {
    void handleNormalizationUpdateDebounce();
  }, [projectProgressStatistics, handleNormalizationUpdateDebounce]);

  if (loading) {
    return (
      <Stack spacing={2} paddingTop={5}>
        <Typography textAlign={'center'}>Loading data...</Typography>
      </Stack>
    );
  }

  if (!loading && !normalizationData) {
    return (
      <Stack spacing={2} paddingTop={5}>
        <Typography textAlign={'center'}>Cannot load data.</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: '1 1 auto', minHeight: 0, paddingY: 2, overflow: 'auto' }}>
      <Alert severity="info">
        <Typography>The normalization logic is designed to more accurately compare between different sites.</Typography>
      </Alert>

      <Box>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          General variables
        </Typography>
        <TableContainer>
          <Table className="simple-table">
            <TableBody>
              <TableRow>
                <TableCell className="th">Total plant conversion costs [USD (United States of America)]</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" noWrap>
                    {NumberHelper.formatNumber(normalizationData?.totalPlantConversionCosts, RoundingPrecision)}
                  </Typography>
                </TableCell>
                <TableCell className="th">Total plant headcount [FTEs]</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" noWrap>
                    {NumberHelper.formatNumber(normalizationData?.totalPlantHeadcount, RoundingPrecision)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="th">
                  Total plant conversion cost (sum of production steps) [USD (United States of America)]
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" noWrap>
                    {NumberHelper.formatNumber(
                      normalizationData?.totalPlantConversionCostProsuctionSteps,
                      RoundingPrecision
                    )}
                  </Typography>
                </TableCell>
                <TableCell className="th">Total plant headcount (Sum o production steps) [FTEs]</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" noWrap>
                    {NumberHelper.formatNumber(
                      normalizationData?.totalPlantHeadcountProductionSteps,
                      RoundingPrecision
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="th">
                  Currency conversion factor [{project.currencyCode} / USD (United States of America)]
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" noWrap>
                    {NumberHelper.formatNumber(normalizationData?.currencyConvertionFactor)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ marginTop: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          Production step breakdown
        </Typography>
        <Card>
          {normalizationData?.data.map((step) => (
            <NormalizationStepAccordion
              key={step.sectionId}
              productionStep={step}
              projectProgressStatistics={projectProgressStatistics}
            />
          ))}
        </Card>
      </Box>
    </Stack>
  );
});

export default NormalizationViewer;
